import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { getPublicShareUrl, getPublicAppUrl } from "@/lib/publicUrl";
import { useShareVisibility } from "@/lib/useShareVisibility";

/**
 * Phase 9.7.16 — 🎉 Push Milestone Toaster
 *
 * Polls /api/notifications and surfaces `push_milestone` notifications
 * with a celebration toast + confetti burst + "Share Your Milestone" CTA.
 *
 * Seen-set tracked in localStorage so each milestone only celebrates once
 * per device. The underlying notification stays in the inbox for review.
 *
 * Mount this once near the top of authenticated layouts (e.g. DashboardPage).
 */

const LS_KEY = "rgm_push_milestones_seen_v1";
const POLL_MS = 45_000;

const loadSeen = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch (_e) {
    return new Set();
  }
};

const saveSeen = (set) => {
  try {
    // Keep last 200 IDs to avoid unbounded growth
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(LS_KEY, JSON.stringify(arr));
  } catch (_e) {
    /* ignore quota errors */
  }
};

const burstConfetti = () => {
  try {
    const fire = (particleRatio, opts) => {
      confetti({
        origin: { y: 0.7 },
        spread: 70,
        startVelocity: 35,
        ticks: 90,
        ...opts,
        particleCount: Math.floor(180 * particleRatio),
      });
    };
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  } catch (_e) {
    /* noop */
  }
};

const buildShareText = (notif) => {
  const title = notif.track_title || "my track";
  // notif.title looks like "🚀 10K Pushed!"
  const milestone = (notif.title || "Pushed milestone").replace(/!$/, "");
  return `${milestone} — "${title}" is being pushed on RGM Studio. Listen + show love 👇`;
};

const handleShare = async (notif) => {
  const text = buildShareText(notif);
  const url = notif.submission_id
    ? getPublicShareUrl(`/track/${notif.submission_id}`)
    : getPublicAppUrl();

  if (navigator.share) {
    try {
      await navigator.share({ title: notif.title || "Milestone", text, url });
      return;
    } catch (_e) {
      /* user cancelled — fall through */
    }
  }
  try {
    await navigator.clipboard.writeText(`${text}\n${url}`);
    toast.success("Share text copied to clipboard ✨");
  } catch (_e) {
    toast.error("Couldn't open the share sheet — try again later");
  }
};

const PushMilestoneToaster = () => {
  const navigate = useNavigate();
  const seenRef = useRef(loadSeen());
  const inFlight = useRef(false);
  // Feb 2026 — CEO share-visibility gate. When "tracks" is OFF, drop
  // the "Share milestone" toast action (milestone shares are Track shares).
  const { canShare } = useShareVisibility();
  const tracksShareOn = canShare("tracks");

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const res = await api.get("/notifications?limit=25");
        const items = res?.data?.notifications || [];
        const milestones = items.filter((n) => n.type === "push_milestone");
        // Oldest first → so the user sees them in chronological order
        milestones.reverse();
        for (const m of milestones) {
          if (cancelled) return;
          const id = m.notification_id;
          if (!id || seenRef.current.has(id)) continue;
          seenRef.current.add(id);
          saveSeen(seenRef.current);

          burstConfetti();
          toast(
            (m.title || "🎉 Milestone reached!"),
            {
              description: m.message || "",
              duration: 9000,
              action: m.submission_id && tracksShareOn
                ? {
                    label: "🚀 Share milestone",
                    onClick: () => handleShare(m),
                  }
                : undefined,
              onAutoClose: () => {},
              cancel: {
                label: "View",
                onClick: () => {
                  if (m.submission_id) {
                    navigate(`/track/${m.submission_id}`);
                  } else {
                    navigate("/notifications");
                  }
                },
              },
              className: "rgm-milestone-toast",
              "data-testid": `push-milestone-toast-${id}`,
            }
          );
          // Light delay between successive bursts so they don't blend
          await new Promise((r) => setTimeout(r, 600));
        }
      } catch (_e) {
        /* fail silently — toaster is a background nicety */
      } finally {
        inFlight.current = false;
      }
    };

    // First poll after a short delay so it doesn't compete with page load
    const initial = setTimeout(poll, 4000);
    const interval = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [navigate, tracksShareOn]);

  return null;
};

export default PushMilestoneToaster;
